import { AuthService } from '@myosh/myosh-login';
import { api } from '../redux/services/api';
import { closeDB } from '../db/odin-db-client';

const authService = new AuthService();

/**
 * Logs out the current user and terminates the dashboard session.
 *
 * This function performs a complete logout process by first terminating
 * any active dashboard session for the specified schema, then calling
 * the authentication service to handle the logout procedure.
 *
 * @param schema - Optional schema identifier for the dashboard session to terminate
 * @returns A Promise that resolves when the logout process is complete
 */
export const logout = async (schema?: string): Promise<void> => {
  await Promise.allSettled([terminateDashboardSession(schema), closeDB()]);
  return authService.logout();
};

/**
 * Terminates an active dashboard session for the specified schema.
 *
 * This function checks if a dashboard session is currently active and attempts to terminate it
 * by dispatching a termination request to the API. It handles the session cleanup gracefully
 * and does not throw errors if termination fails.
 *
 * @param schema - The schema identifier for which to terminate the dashboard session.
 *                 If not provided, the function returns early without performing any action.
 *
 * @returns A Promise that resolves when the termination attempt is complete.
 *          The Promise resolves regardless of whether the termination was successful.
 *
 * @remarks
 * - The function dynamically imports the Redux store to avoid circular dependency issues
 * - Session termination errors are silently caught as there's no recovery mechanism
 * - Requires `dashboardSessionRegistered` and `sessionId-${schema}` to be present in localStorage
 */
export const terminateDashboardSession = async (schema?: string): Promise<void> => {
  if (schema) {
    // terminate the dashboard session if it exists
    const dashboardSessionRegistered = localStorage.getItem('dashboardSessionRegistered');
    if (dashboardSessionRegistered == 'true') {
      const sessionId = localStorage.getItem(`sessionId-${schema}`);
      if (!sessionId) {
        return;
      }

      try {
        const { default: store } = await import('../redux/store'); // dynamically import store to avoid circular dependency issues
        await store.dispatch(api.endpoints.terminateDashboardSession.initiate(sessionId)).unwrap();
      } catch (_) {
        // we have no way to recover at this point
      } finally {
        localStorage.removeItem('dashboardSessionRegistered');
      }
    }
  }
};
